import { useState } from 'react';
import {
  useAdminVerificationUsers,
  useAcceptAdminVerificationUser,
  useRejectAdminVerificationUser,
} from '@/hooks/use-admin';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ShieldIcon } from '@/components/layout/icons';
import type { AdminVerificationUserRow, UserStatus } from '@shared/types/api';

const ROLE_LABELS: Record<string, { label: string; badgeClass: string }> = {
  SUPER_ADMIN: { label: 'Super Admin', badgeClass: 'bg-purple-100 text-purple-800 border-purple-200' },
  OH_SERVICE_MANAGER: { label: 'OH / Service Manager', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
  ASSIGNER: { label: 'Assigner', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  CASE_EXECUTIVE: { label: 'Case Executive', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  ASSOCIATE_CONTROLLING_EXECUTIVE: { label: 'Associate Controlling Executive', badgeClass: 'bg-teal-100 text-teal-800 border-teal-200' },
  CUSTOMER_SUPPORT_EXECUTIVE: { label: 'Customer Support Executive', badgeClass: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  FINANCE_EXECUTIVE: { label: 'Finance Executive', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' },
  AUDITOR_COMPLIANCE_REVIEWER: { label: 'Auditor / Compliance Reviewer', badgeClass: 'bg-slate-100 text-slate-800 border-slate-200' },
  OPS_ADMIN: { label: 'Operations Admin', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
  READ_ONLY_ADMIN: { label: 'Read-Only Admin', badgeClass: 'bg-slate-100 text-slate-800 border-slate-200' },
  ASSIGNER_ADMIN: { label: 'Assigner Admin', badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
};

export function AdminVerificationPage() {
  const [tab, setTab] = useState<'PENDING' | 'REJECTED' | 'ACTIVE' | 'ALL'>('PENDING');

  // Query parameter mapped to server UserStatus filter
  const statusFilter: UserStatus | undefined =
    tab === 'PENDING'
      ? 'PENDING_ADMIN_APPROVAL'
      : tab === 'REJECTED'
      ? 'REJECTED'
      : tab === 'ACTIVE'
      ? 'ACTIVE'
      : undefined;

  const { data: users = [], isLoading, isError, refetch } = useAdminVerificationUsers(statusFilter);

  const acceptMutation = useAcceptAdminVerificationUser();
  const rejectMutation = useRejectAdminVerificationUser();

  // Modals state
  const [acceptTarget, setAcceptTarget] = useState<AdminVerificationUserRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminVerificationUserRow | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [detailTarget, setDetailTarget] = useState<AdminVerificationUserRow | null>(null);

  // Filter out any Super Admin accounts (Super Admins are review administrators, not applicants)
  const operationalUsers = users.filter((u) => u.adminRole !== 'SUPER_ADMIN');

  // Compute count badges from operational applicants
  const { data: allUsers = [] } = useAdminVerificationUsers(undefined);
  const allOperationalUsers = allUsers.filter((u) => u.adminRole !== 'SUPER_ADMIN');
  const pendingCount = allOperationalUsers.filter((u) => u.status === 'PENDING_ADMIN_APPROVAL').length;
  const rejectedCount = allOperationalUsers.filter((u) => u.status === 'REJECTED').length;
  const activeCount = allOperationalUsers.filter((u) => u.status === 'ACTIVE').length;

  function handleConfirmAccept() {
    if (!acceptTarget) return;
    acceptMutation.mutate(acceptTarget.id, {
      onSuccess: () => setAcceptTarget(null),
    });
  }

  function handleConfirmReject(e: React.FormEvent) {
    e.preventDefault();
    if (!rejectTarget || !rejectionReason.trim()) return;
    rejectMutation.mutate(
      { id: rejectTarget.id, reason: rejectionReason.trim() },
      {
        onSuccess: () => {
          setRejectTarget(null);
          setRejectionReason('');
        },
      },
    );
  }

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">Admin Account Verifications</h1>
            <span className="inline-flex items-center gap-1 rounded bg-brand-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
              <ShieldIcon className="h-3 w-3" />
              Super Admin Only
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Review registration applications, assign operational authorities, or decline requests with feedback.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-6 overflow-x-auto no-scrollbar scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200">
        <button
          onClick={() => setTab('PENDING')}
          className={`shrink-0 pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            tab === 'PENDING'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Pending Verification
          {pendingCount > 0 && (
            <span className="rounded-full bg-amber-500 text-white px-2 py-0.5 text-xs font-bold">
              {pendingCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setTab('REJECTED')}
          className={`shrink-0 pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            tab === 'REJECTED'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Declined Applications
          {rejectedCount > 0 && (
            <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs font-bold">
              {rejectedCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setTab('ACTIVE')}
          className={`shrink-0 pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            tab === 'ACTIVE'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Active Staff ({activeCount})
        </button>

        <button
          onClick={() => setTab('ALL')}
          className={`shrink-0 pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            tab === 'ALL'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All Applications ({allOperationalUsers.length})
        </button>
      </div>

      {/* Content Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-xs text-gray-500">
            Loading admin applicants…
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-xs text-red-500">
            Failed to load admin verification list.{' '}
            <button onClick={() => refetch()} className="underline font-semibold ml-1">
              Retry
            </button>
          </div>
        ) : operationalUsers.length === 0 ? (
          <div className="py-16 text-center space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <ShieldIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-bold text-gray-900">
              {tab === 'PENDING'
                ? 'No Pending Verifications'
                : tab === 'REJECTED'
                ? 'No Declined Applications'
                : tab === 'ACTIVE'
                ? 'No Active Staff'
                : 'No Applications Found'}
            </h3>
            <p className="mx-auto max-w-md text-xs text-gray-500">
              {tab === 'PENDING'
                ? activeCount > 0
                  ? `All submitted admin registrations have been reviewed. There are currently ${activeCount} active operational staff members.`
                  : 'No admin applicants are currently awaiting approval.'
                : tab === 'REJECTED'
                ? 'No admin applications have been declined.'
                : 'No admin applicants found in this category.'}
            </p>
            {tab === 'PENDING' && activeCount > 0 && (
              <Button
                variant="outline"
                className="mt-2 text-xs px-3 py-1.5"
                onClick={() => setTab('ACTIVE')}
              >
                View Active Staff ({activeCount})
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[750px] text-left text-sm text-gray-700">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="py-3.5 px-4 whitespace-nowrap">Applicant</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Mobile Number</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Applied Role</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Status</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Submitted Date</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {operationalUsers.map((u) => {
                  const roleMeta =
                    ROLE_LABELS[u.adminRole ?? ''] || {
                      label: u.adminRole?.replace(/_/g, ' ') || 'Admin',
                      badgeClass: 'bg-gray-100 text-gray-800 border-gray-200',
                    };

                  return (
                    <tr key={u.id} className="hover:bg-gray-50/75 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-800">
                            {(u.firstName?.[0] || u.email[0]).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {u.firstName || u.lastName
                                ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
                                : 'Unnamed Admin'}
                            </div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-sm text-gray-800">
                        {u.mobile || '—'}
                      </td>

                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-block rounded-md border px-2.5 py-1 text-xs font-semibold ${roleMeta.badgeClass}`}
                        >
                          {roleMeta.label}
                        </span>
                      </td>

                      <td className="py-3.5 px-4">
                        {u.status === 'PENDING_ADMIN_APPROVAL' ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                            Pending Review
                          </span>
                        ) : u.status === 'REJECTED' ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-700 cursor-pointer"
                            onClick={() => setDetailTarget(u)}
                            title={u.rejectionReason ?? ''}
                          >
                            Declined
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-semibold text-green-700">
                            Active
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-gray-600 text-sm">
                        {new Date(u.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDetailTarget(u)}
                            className="rounded px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            View
                          </button>

                          {u.status !== 'ACTIVE' && (
                            <button
                              type="button"
                              onClick={() => setAcceptTarget(u)}
                              disabled={acceptMutation.isPending}
                              className="rounded bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 text-xs font-semibold shadow-2xs transition-colors"
                            >
                              Accept
                            </button>
                          )}

                          {u.status !== 'REJECTED' && (
                            <button
                              type="button"
                              onClick={() => {
                                setRejectTarget(u);
                                setRejectionReason('');
                              }}
                              disabled={rejectMutation.isPending}
                              className="rounded bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-3 py-1 text-xs font-semibold transition-colors"
                            >
                              Decline
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Accept Confirmation Dialog */}
      <Dialog
        open={!!acceptTarget}
        onClose={() => setAcceptTarget(null)}
        title="Accept Admin Account"
        footer={
          <>
            <Button variant="outline" onClick={() => setAcceptTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAccept}
              disabled={acceptMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {acceptMutation.isPending ? 'Approving…' : 'Confirm & Activate'}
            </Button>
          </>
        }
      >
        <div className="py-2 text-xs text-gray-600 space-y-2">
          <p>
            Are you sure you want to approve{' '}
            <strong className="text-gray-900">
              {acceptTarget?.firstName} {acceptTarget?.lastName}
            </strong>{' '}
            ({acceptTarget?.email})?
          </p>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-900">
            Role to activate:{' '}
            <strong className="font-bold">
              {ROLE_LABELS[acceptTarget?.adminRole ?? '']?.label || acceptTarget?.adminRole}
            </strong>
          </div>
          <p className="text-[11px] text-gray-500">
            Once accepted, this user will gain immediate operational access corresponding to their role.
          </p>
        </div>
      </Dialog>

      {/* Decline / Reject Dialog with Mandatory Reason */}
      <Dialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Decline Admin Application"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmReject}
              disabled={rejectMutation.isPending || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {rejectMutation.isPending ? 'Declining…' : 'Decline Application'}
            </Button>
          </>
        }
      >
        <div className="py-2 space-y-3">
          <p className="text-xs text-gray-600">
            Specify the feedback or reason for declining{' '}
            <strong className="text-gray-900">{rejectTarget?.email}</strong>. The applicant will see this reason in their dashboard, allowing them to correct their profile and resubmit.
          </p>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Rejection Reason / Guidance <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              placeholder="e.g. Please select Case Executive instead of Assigner, or provide your direct work mobile."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 p-2.5 text-xs text-gray-900 focus:border-red-500 focus:outline-hidden focus:ring-1 focus:ring-red-500"
              required
            />
          </div>
        </div>
      </Dialog>

      {/* Applicant Detail Dialog */}
      <Dialog
        open={!!detailTarget}
        onClose={() => setDetailTarget(null)}
        title="Applicant Profile Details"
        footer={
          <Button variant="outline" onClick={() => setDetailTarget(null)}>
            Close
          </Button>
        }
      >
        {detailTarget && (
          <div className="py-2 space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Name</span>
                <span className="font-semibold text-gray-900">
                  {detailTarget.firstName} {detailTarget.lastName}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Email</span>
                <span className="font-semibold text-gray-900">{detailTarget.email}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Mobile</span>
                <span className="font-semibold text-gray-900">{detailTarget.mobile || '—'}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Role Requested</span>
                <span className="font-bold text-brand-700">
                  {ROLE_LABELS[detailTarget.adminRole ?? '']?.label || detailTarget.adminRole}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Status</span>
                <span className="font-semibold">{detailTarget.status}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Registered At</span>
                <span className="text-gray-700">
                  {new Date(detailTarget.createdAt).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {detailTarget.rejectionReason && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-900">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-red-700">
                  Recorded Rejection Reason:
                </span>
                <p className="mt-1 font-medium">{detailTarget.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
